import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { B2BWarmupSettings } from "@/types/mailbox.types";
import { useGetB2BWarmupSettings, useSaveB2BWarmupSettings } from "@/hooks/use-mailbox";
import { extractErrorMessage, validEmailFormatSchema } from "@/lib/utils";
import { toast } from "sonner";
import {
  Info,
  RefreshCw,
  Copy,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Shuffle,
  Minus,
  Undo2,
  CalendarDays,
  CalendarRange,
  Calendar,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { authProviderIcons } from "@/pages/mailbox/esp-providers";

/** Channel/provider keys for mix (match esp-providers icon keys) */
const CHANNEL_PROVIDER_KEYS = [
  "gmailPercent",
  "googleWorkspacePercent",
  "microsoft365Percent",
  "yahooPercent",
  "outlookPercent",
  "zohoPercent",
] as const;

/** Map mailbox.warmupConfig.espBreakdown keys to form *Percent keys */
const ESP_BREAKDOWN_TO_PERCENT: Record<string, (typeof CHANNEL_PROVIDER_KEYS)[number]> = {
  googleWorkspace: "googleWorkspacePercent",
  gmail: "gmailPercent",
  microsoft365: "microsoft365Percent",
  outlook: "outlookPercent",
  yahoo: "yahooPercent",
  zoho: "zohoPercent",
};

const CHANNEL_PROVIDERS: { key: (typeof CHANNEL_PROVIDER_KEYS)[number]; label: string; iconKey: string }[] = [
  { key: "googleWorkspacePercent", label: "Google Workspace", iconKey: "google_workspace" },
  { key: "microsoft365Percent", label: "Microsoft 365", iconKey: "microsoft_365" },
  { key: "gmailPercent", label: "Personal Gmail", iconKey: "gmail" },
  { key: "outlookPercent", label: "Personal Outlook", iconKey: "outlook" },
  { key: "yahooPercent", label: "Personal Yahoo", iconKey: "yahoo" },
  { key: "zohoPercent", label: "Personal Zoho", iconKey: "zoho" },
];

function getChannelMixTotal(form: B2BWarmupSettings): number {
  return CHANNEL_PROVIDER_KEYS.reduce(
    (sum, key) => sum + (typeof form[key] === "number" ? form[key]! : 0),
    0
  );
}

const MAX_PERCENT_PERSONAL_ESP = 10;
const MAX_PERCENT_WORKSPACE_ESP = 50;

function getMaxPercentForProvider(
  key: (typeof CHANNEL_PROVIDER_KEYS)[number]
): number {
  if (key === "googleWorkspacePercent" || key === "microsoft365Percent") {
    return MAX_PERCENT_WORKSPACE_ESP;
  }
  return MAX_PERCENT_PERSONAL_ESP;
}

function getMaxAllowedForProvider(
  form: B2BWarmupSettings,
  currentKey: (typeof CHANNEL_PROVIDER_KEYS)[number]
): number {
  const otherSum = CHANNEL_PROVIDER_KEYS.reduce(
    (sum, key) =>
      key === currentKey ? sum : sum + (typeof form[key] === "number" ? form[key]! : 0),
    0
  );
  const providerMax = getMaxPercentForProvider(currentKey);
  return Math.min(providerMax, Math.max(0, 100 - otherSum));
}

/** Dummy default values shown when "Default" mode is selected (or when API returns nothing). Unused strategy fields are 0. */
const DUMMY_DEFAULT_SETTINGS: B2BWarmupSettings = {
  settingsMode: "default",
  gmailPercent: 10,
  googleWorkspacePercent: 35,
  microsoft365Percent: 35,
  yahooPercent: 10,
  outlookPercent: 10,
  zohoPercent: 0,
  replyToEnabled: false,
  replyToEmails: "",
  warmupStrategy: "progressive",
  progressiveStartingVolume: 3,
  progressiveIncreaseBy: 3,
  progressiveMaxLimit: 25,
  randomMinLimit: 0,
  randomMaxLimit: 0,
  flatConstantEmails: 0,
  increasePerDay: 0,
  currentCounter: 0,
  dailyWarmupLimit: 40,
  replyRatePercent: 30,
  customTag: "",
  defaultTag: "inboxdoctor",
  enableCustomTrackingDomain: false,
  trackingDomainOrSubdomain: "",
  weekdaysOnly: false,
  sendOnDays: undefined,
  readEmulation: true,
  warmupCustomTrackingDomain: false,
  trackingDomainId: "",
  timezone: "Asia/Kolkata",
  openRatesPercent: 95,
  spamProtectionPercent: 100,
  markImportantPercent: 20,
  timeGapMinutes: 5,
  deliveryWindowStartMinutes: 6 * 60, // 6:00 AM
  deliveryWindowEndMinutes: 20 * 60,  // 8:00 PM
};

/** Normalize payload: currentCounter = 0 for fresh start; zero out strategy fields not used by the selected strategy. */
function normalizePayloadForSave(form: B2BWarmupSettings, settingsMode: "default" | "manual"): B2BWarmupSettings {
  const strategy = form.warmupStrategy ?? "progressive";
  const base = { ...form, settingsMode, currentCounter: 0 };

  if (strategy === "progressive") {
    return {
      ...base,
      randomMinLimit: 0,
      randomMaxLimit: 0,
      flatConstantEmails: 0,
      increasePerDay: 0,
    };
  }
  if (strategy === "random") {
    return {
      ...base,
      progressiveStartingVolume: 0,
      progressiveIncreaseBy: 0,
      progressiveMaxLimit: 0,
      flatConstantEmails: 0,
      increasePerDay: 0,
    };
  }
  // flat
  return {
    ...base,
    progressiveStartingVolume: 0,
    progressiveIncreaseBy: 0,
    progressiveMaxLimit: 0,
    randomMinLimit: 0,
    randomMaxLimit: 0,
    increasePerDay: 0,
  };
}

const TIMEZONE_OPTIONS = [
  { value: "Asia/Kolkata", label: "(GMT+05:30) Asia/Kolkata" },
  { value: "America/New_York", label: "(GMT-05:00) America/New_York" },
  { value: "America/Los_Angeles", label: "(GMT-08:00) America/Los_Angeles" },
  { value: "Europe/London", label: "(GMT+00:00) Europe/London" },
];

const TIME_GAP_OPTIONS = [
  { value: "5", label: "5 minutes" },
  { value: "10", label: "10 minutes" },
  { value: "15", label: "15 minutes" },
  { value: "20", label: "20 minutes" },
  { value: "25", label: "25 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "45", label: "45 minutes" },
  { value: "60", label: "1 hour" },
];

const MINUTES_PER_DAY = 24 * 60; // 1440
const DEFAULT_DELIVERY_WINDOW_START = 6 * 60; // 6:00 AM
const DEFAULT_DELIVERY_WINDOW_END = 20 * 60; // 8:00 PM (20h from midnight)

/** Build time options every 30 minutes: 0, 30, 60, ... 1410 (value), "12:00 AM", ... "11:30 PM" (label). */
function getDeliveryWindowTimeOptions(): { value: number; label: string }[] {
  const out: { value: number; label: string }[] = [];
  for (let m = 0; m < MINUTES_PER_DAY; m += 30) {
    const h = Math.floor(m / 60);
    const min = m % 60;
    const isPm = h >= 12;
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const label = `${h12}:${min === 0 ? "00" : "30"} ${isPm ? "PM" : "AM"}`;
    out.push({ value: m, label });
  }
  out.push({ value: MINUTES_PER_DAY, label: "12:00 AM (midnight)" });
  return out;
}
const DELIVERY_WINDOW_TIME_OPTIONS = getDeliveryWindowTimeOptions();

/** Format minutes as human-readable duration (e.g. "2h 30m" or "24 hours"). */
function formatWindowDuration(minutes: number): string {
  if (minutes <= 0) return "0 hours";
  if (minutes % 60 === 0) return `${minutes / 60} hours`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** Available minutes per day from delivery window. Default 24h when not set. */
function getAvailableMinutesPerDay(form: B2BWarmupSettings): number {
  const start = form.deliveryWindowStartMinutes ?? DEFAULT_DELIVERY_WINDOW_START;
  const end = form.deliveryWindowEndMinutes ?? DEFAULT_DELIVERY_WINDOW_END;
  if (end > start) return end - start;
  if (end < start) return MINUTES_PER_DAY - start + end; // overnight
  return MINUTES_PER_DAY;
}

/** Max emails per day from current strategy and plan cap */
function getEffectiveMaxEmailsPerDay(
  form: B2BWarmupSettings,
  planMaxDailyLimit?: number
): number {
  const strategy = form.warmupStrategy ?? "progressive";
  let max = 0;
  if (strategy === "progressive") max = form.progressiveMaxLimit ?? 0;
  else if (strategy === "random") max = form.randomMaxLimit ?? 0;
  else if (strategy === "flat") max = form.flatConstantEmails ?? 0;
  if (typeof planMaxDailyLimit === "number" && planMaxDailyLimit > 0 && max > planMaxDailyLimit) {
    return planMaxDailyLimit;
  }
  return max;
}

/** Max time gap (minutes) so that floor(availableMinutes / gap) >= maxEmailsPerDay. */
function getMaxAllowedTimeGapMinutes(maxEmailsPerDay: number, availableMinutes: number): number {
  if (maxEmailsPerDay <= 0) return 60;
  return Math.floor(availableMinutes / maxEmailsPerDay);
}

/** 0=Sun, 1=Mon, ... 6=Sat. Weekdays = Mon–Fri, All days = every day */
const WEEKDAY_DAYS = [1, 2, 3, 4, 5];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
const DAY_LABELS: Record<number, string> = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
};

const TRACKING_DOMAIN_PLACEHOLDER_VALUE = "__none__";
const TRACKING_DOMAIN_ADD_NEW_VALUE = "";
const TRACKING_DOMAIN_OPTIONS = [
  { value: TRACKING_DOMAIN_PLACEHOLDER_VALUE, label: "Select a tracking domain" },
  { value: TRACKING_DOMAIN_ADD_NEW_VALUE, label: "Add new domain" },
];

interface WarmupB2BSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mailboxId: string;
  mailboxEmail?: string;
  /** Sender/mailbox first name (shown with email). */
  mailboxFirstName?: string;
  /** Sender/mailbox last name (shown with email). */
  mailboxLastName?: string;
  /** When > 1, show "Configuring warmup for X mailboxes" instead of the single email. */
  selectedMailboxCount?: number;
  /** If false, dialog will close immediately and show toast to assign a plan first. */
  planAssigned?: boolean;
  /** Max daily limit from plan; strategy limits (progressive max, random max, flat constant, daily warmup limit) must not exceed this. */
  planMaxDailyLimit?: number;
  /** When provided, called with settings to enable warmup (parent calls enable-warmup-settings API). No saveSettings call. */
  onSaveAndEnableWarmup?: (settings: B2BWarmupSettings) => void | Promise<void>;
  /** ESP distribution from mailbox.warmupConfig.espBreakdown (source of truth from mailbox API). When provided, overrides ESP % in form so dialog shows actual saved values. */
  espBreakdown?: Record<string, number> | null;
  /** Other warmup fields from mailbox (customWarmupPlan + root). When provided, overrides form so dialog shows actual saved strategy, limits, timezone, etc. */
  mailboxWarmupOverrides?: Partial<B2BWarmupSettings> | null;
  /** When provided (single mailbox), called on Save with updated first/last name so parent can persist (e.g. update mailbox). */
  onMailboxNameChange?: (firstName: string, lastName: string) => void | Promise<void>;
}

function generateDefaultTag(): string {
  return Math.random().toString(36).toUpperCase().slice(2, 9);
}

type WarmupStrategyType = "progressive" | "random" | "flat";

/** Generate daily send volumes for preview (e.g. 14 days) */
function getProgressiveBarData(
  start: number,
  increaseBy: number,
  maxLimit: number,
  days = 14
): number[] {
  const out: number[] = [];
  let v = start;
  for (let i = 0; i < days; i++) {
    out.push(Math.min(v, maxLimit));
    v += increaseBy;
  }
  return out;
}

/** Deterministic "random" for stable preview: same min/max/day index => same value */
function getRandomBarData(minLimit: number, maxLimit: number, days = 12): number[] {
  const range = maxLimit - minLimit + 1;
  const out: number[] = [];
  for (let i = 0; i < days; i++) {
    const seed = (minLimit * 7 + maxLimit * 11 + i * 13) % 997;
    out.push(minLimit + (seed % range));
  }
  return out;
}

function getFlatBarData(constant: number, days = 10): number[] {
  return Array(days).fill(constant);
}

const BAR_PREVIEW_HEIGHT = 80;
const Y_AXIS_WIDTH = 28;
const X_AXIS_HEIGHT = 20;

function WarmupBarPreview({
  data,
  maxValue,
  todayIndex = -1,
  maxReachedIndex = -1,
  variant = "progressive",
}: {
  data: number[];
  maxValue: number;
  todayIndex?: number;
  maxReachedIndex?: number;
  variant?: "progressive" | "random" | "flat";
}) {
  const scale = maxValue > 0 ? BAR_PREVIEW_HEIGHT / maxValue : 0;
  const yTicks = [
    maxValue,
    ...(maxValue > 1 ? [Math.round(maxValue / 2)] : []),
    0,
  ].filter((v, i, a) => a.indexOf(v) === i).sort((a, b) => b - a);

  const showEveryNthLabel = data.length > 10 ? 2 : 1;

  return (
    <div className="space-y-0">
      <div className="flex items-stretch gap-1">
        {/* Y-axis with label */}
        <div
          className="flex flex-col shrink-0 text-right pr-1.5 text-[10px] text-muted-foreground tabular-nums"
          style={{ width: Y_AXIS_WIDTH, height: BAR_PREVIEW_HEIGHT }}
        >
          <span className="leading-none mb-auto" title="Y-axis: Emails">Emails</span>
          <div className="flex flex-col justify-between flex-1 py-0.5">
            {yTicks.map((tick) => (
              <span key={tick}>{tick}</span>
            ))}
          </div>
        </div>
        {/* Chart area (bars) + X-axis */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div
            className="flex items-end gap-0.5 w-full border-b border-border"
            style={{ height: BAR_PREVIEW_HEIGHT }}
          >
            {data.map((value, i) => {
              const isToday = i === todayIndex;
              const isAtMax = variant === "progressive" ? i >= maxReachedIndex && maxReachedIndex >= 0 : value >= maxValue;
              const isRamping = variant === "progressive" && maxReachedIndex >= 0 && i < maxReachedIndex;
              return (
                <div
                  key={i}
                  className={cn(
                    "flex-1 min-w-[6px] rounded-t transition-colors",
                    isToday && "ring-2 ring-primary ring-offset-1 ring-offset-background",
                    variant === "flat" && "bg-primary/80",
                    variant === "random" && "bg-primary/70",
                    variant === "progressive" && isAtMax && "bg-primary",
                    variant === "progressive" && isRamping && "bg-primary/60",
                    variant === "progressive" && !isRamping && !isAtMax && "bg-primary/50"
                  )}
                  style={{ height: Math.max(2, value * scale) }}
                  title={`Day ${i + 1}: ${value} emails`}
                />
              );
            })}
          </div>
          {/* X-axis: day numbers */}
          <div
            className="flex gap-0.5 w-full pt-0.5"
            style={{ minHeight: X_AXIS_HEIGHT }}
          >
            {data.map((_, i) => (
              <div
                key={i}
                className="flex-1 min-w-[6px] text-center text-[10px] text-muted-foreground tabular-nums truncate"
                title={`Day ${i + 1}`}
              >
                {i % showEveryNthLabel === 0 ? (i === todayIndex ? "Today" : i + 1) : ""}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex gap-1 pt-0.5" style={{ paddingLeft: Y_AXIS_WIDTH + 4 }}>
        <span className="text-[10px] text-muted-foreground">Day</span>
      </div>
      {variant === "progressive" && maxValue > 0 && maxReachedIndex >= 0 && (
        <p className="text-[10px] text-muted-foreground pt-0.5" style={{ paddingLeft: Y_AXIS_WIDTH + 4 }}>
          Max at day {maxReachedIndex + 1} · {maxValue}/day
        </p>
      )}
    </div>
  );
}

export function WarmupB2BSettingsDialog({
  open,
  onOpenChange,
  mailboxId,
  mailboxEmail,
  mailboxFirstName,
  mailboxLastName,
  selectedMailboxCount,
  planAssigned = true,
  planMaxDailyLimit,
  onSaveAndEnableWarmup,
  espBreakdown,
  mailboxWarmupOverrides,
  onMailboxNameChange,
}: WarmupB2BSettingsDialogProps) {
  const [settingsMode, setSettingsMode] = useState<"default" | "manual">(
    "manual"
  );
  const [form, setForm] = useState<B2BWarmupSettings>(() => ({
    ...DUMMY_DEFAULT_SETTINGS,
    settingsMode: "manual",
  }));
  const [advancedOpen, setAdvancedOpen] = useState(true);
  const [customTrackingDomainInput, setCustomTrackingDomainInput] = useState("");
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");

  const { data: existingSettings } = useGetB2BWarmupSettings(
    open && mailboxId ? mailboxId : null
  );
  const { mutateAsync: saveSettings, isPending: isSaving } =
    useSaveB2BWarmupSettings();

  const isDefaultMode = settingsMode === "default";
  const isReadOnly = isDefaultMode;

  // If opened without a plan assigned, close immediately and show toast
  useEffect(() => {
    if (open && !planAssigned) {
      onOpenChange(false);
      toast.error("Please assign a plan to this mailbox first.");
    }
  }, [open, planAssigned, onOpenChange]);

  // Sync editable name when dialog opens (single mailbox)
  useEffect(() => {
    if (open) {
      setEditFirstName(mailboxFirstName ?? "");
      setEditLastName(mailboxLastName ?? "");
    }
  }, [open, mailboxFirstName, mailboxLastName]);

  // When dialog opens, populate form from API settings, mailbox espBreakdown, and other mailbox warmup fields.
  // Default warmup strategy limits (progressive max, random max, flat) use planMaxDailyLimit when available.
  useEffect(() => {
    if (!open) return;
    const base = existingSettings
      ? { ...DUMMY_DEFAULT_SETTINGS, ...existingSettings }
      : { ...DUMMY_DEFAULT_SETTINGS };

    // Override ESP distribution from mailbox.warmupConfig.espBreakdown when available
    let espOverrides: Partial<B2BWarmupSettings> = {};
    if (espBreakdown && typeof espBreakdown === "object") {
      for (const [key, value] of Object.entries(espBreakdown)) {
        const formKey = ESP_BREAKDOWN_TO_PERCENT[key];
        if (formKey != null && typeof value === "number") {
          espOverrides[formKey] = value;
        }
      }
    }

    // Override strategy, limits, timezone, etc. from mailbox (customWarmupPlan + root)
    const overrides = mailboxWarmupOverrides && typeof mailboxWarmupOverrides === "object"
      ? { ...mailboxWarmupOverrides }
      : {};
    const mode = overrides.settingsMode ?? base.settingsMode ?? "manual";
    setSettingsMode(mode);

    // Derive sendOnDays from weekdaysOnly if not provided
    let sendOnDays = overrides.sendOnDays ?? base.sendOnDays;
    if (sendOnDays == null || !Array.isArray(sendOnDays)) {
      sendOnDays = (overrides.weekdaysOnly ?? base.weekdaysOnly) ? WEEKDAY_DAYS : ALL_DAYS;
    }

    const planMax = typeof planMaxDailyLimit === "number" && planMaxDailyLimit > 0 ? planMaxDailyLimit : null;
    const merged: B2BWarmupSettings = {
      ...base,
      ...espOverrides,
      ...overrides,
      settingsMode: mode,
      sendOnDays,
    };
    if (planMax != null) {
      merged.progressiveMaxLimit = Math.min(merged.progressiveMaxLimit ?? planMax, planMax);
      merged.randomMaxLimit = Math.min(merged.randomMaxLimit ?? planMax, planMax);
      merged.flatConstantEmails = Math.min(merged.flatConstantEmails ?? planMax, planMax);
    } else {
      merged.progressiveMaxLimit = merged.progressiveMaxLimit ?? 25;
    }
    // Default timezone to Asia/Kolkata when unset or when API/backend returns America/New_York as legacy default
    const effectiveTimezone = merged.timezone && merged.timezone !== "America/New_York"
      ? merged.timezone
      : "Asia/Kolkata";
    merged.timezone = effectiveTimezone;
    setForm(merged);
  }, [open, existingSettings, espBreakdown, mailboxWarmupOverrides, planMaxDailyLimit]);

  // When strategy, daily limit, or delivery window changes, cap time gap so it fits (availableMinutes / gap >= max emails)
  useEffect(() => {
    const effectiveMaxEmails = getEffectiveMaxEmailsPerDay(form, planMaxDailyLimit);
    if (effectiveMaxEmails <= 0) return;
    const availableMinutes = getAvailableMinutesPerDay(form);
    const maxAllowedGap = getMaxAllowedTimeGapMinutes(effectiveMaxEmails, availableMinutes);
    const current = form.timeGapMinutes ?? 5;
    if (current <= maxAllowedGap) return;
    setForm((prev) => ({ ...prev, timeGapMinutes: maxAllowedGap }));
  }, [
    form.warmupStrategy,
    form.progressiveMaxLimit,
    form.randomMaxLimit,
    form.flatConstantEmails,
    form.timeGapMinutes,
    form.deliveryWindowStartMinutes,
    form.deliveryWindowEndMinutes,
    planMaxDailyLimit,
  ]);

  const switchToDefault = useCallback(() => {
    const defaultMax = typeof planMaxDailyLimit === "number" && planMaxDailyLimit > 0 ? planMaxDailyLimit : 25;
    setSettingsMode("default");
    setForm((prev) => ({
      ...DUMMY_DEFAULT_SETTINGS,
      settingsMode: "default",
      defaultTag: prev.defaultTag || DUMMY_DEFAULT_SETTINGS.defaultTag,
      progressiveMaxLimit: defaultMax,
      randomMaxLimit: 0,
      flatConstantEmails: 0,
    }));
  }, [planMaxDailyLimit]);

  const switchToManual = useCallback(() => {
    setSettingsMode("manual");
    setForm((prev) => ({
      ...prev,
      settingsMode: "manual",
    }));
  }, []);

  const updateField = useCallback(
    (key: keyof B2BWarmupSettings, value: B2BWarmupSettings[keyof B2BWarmupSettings]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleRefreshDefaultTag = useCallback(() => {
    updateField("defaultTag", generateDefaultTag());
  }, [updateField]);

  const handleCopyDnsValue = useCallback(() => {
    navigator.clipboard.writeText("trackinboxdoctor.com");
    toast.success("Copied to clipboard");
  }, []);

  const handleResetCustomTracking = useCallback(() => {
    updateField("enableCustomTrackingDomain", false);
    updateField("trackingDomainId", undefined);
    setCustomTrackingDomainInput("");
  }, [updateField]);

  /** Inline validation: one message per section (null = no error). Save enabled when all are null. */
  const inlineErrors = useMemo((): {
    esp: string | null;
    replyTo: string | null;
    strategy: string | null;
    tracking: string | null;
    schedule: string | null;
    deliveryWindow: string | null;
    timeGap: string | null;
  } => {
    const total = getChannelMixTotal(form);
    const espError =
      total !== 100
        ? `ESP distribution must total 100%. Current total: ${total}%.`
        : CHANNEL_PROVIDER_KEYS.some((key) => {
            const value = form[key];
            return typeof value === "number" && value > getMaxPercentForProvider(key);
          })
          ? "Personal ESPs (Gmail, Outlook, Yahoo, Zoho) max 10% each. Google Workspace and Microsoft 365 max 50% each."
          : null;

    const replyToError =
      form.replyToEnabled && form.replyToEmails && !validEmailFormatSchema.safeParse(form.replyToEmails.trim()).success
        ? "Please enter a valid reply-to email address."
        : null;

    let strategyError: string | null = null;
    if (typeof planMaxDailyLimit === "number" && planMaxDailyLimit > 0) {
      const strategy = form.warmupStrategy ?? "progressive";
      if (strategy === "progressive") {
        const max = form.progressiveMaxLimit ?? 0;
        if (max > planMaxDailyLimit) strategyError = `Progressive max limit (${max}) cannot exceed plan limit (${planMaxDailyLimit}).`;
      } else if (strategy === "random") {
        const max = form.randomMaxLimit ?? 0;
        if (max > planMaxDailyLimit) strategyError = `Random max per day (${max}) cannot exceed plan limit (${planMaxDailyLimit}).`;
      } else if (strategy === "flat") {
        const c = form.flatConstantEmails ?? 0;
        if (c > planMaxDailyLimit) strategyError = `Flat emails per day (${c}) cannot exceed plan limit (${planMaxDailyLimit}).`;
      }
    }

    const trackingError =
      form.enableCustomTrackingDomain &&
      !(
        (form.trackingDomainId && form.trackingDomainId !== TRACKING_DOMAIN_PLACEHOLDER_VALUE && form.trackingDomainId !== TRACKING_DOMAIN_ADD_NEW_VALUE) ||
        (form.trackingDomainId === TRACKING_DOMAIN_ADD_NEW_VALUE && customTrackingDomainInput.trim().length > 0)
      )
        ? "Please select a tracking domain or add a new one."
        : null;

    const isAllDays = form.sendOnDays?.length === 7 && [0, 1, 2, 3, 4, 5, 6].every((d) => form.sendOnDays?.includes(d));
    const isCustomSchedule = !form.weekdaysOnly && !isAllDays;
    const scheduleError =
      isCustomSchedule && (!form.sendOnDays || form.sendOnDays.length === 0)
        ? "Select at least one day for custom schedule."
        : null;

    const start = form.deliveryWindowStartMinutes ?? DEFAULT_DELIVERY_WINDOW_START;
    const end = form.deliveryWindowEndMinutes ?? DEFAULT_DELIVERY_WINDOW_END;
    const windowMinutes = end > start ? end - start : end < start ? MINUTES_PER_DAY - start + end : MINUTES_PER_DAY;
    const availableMinutes = getAvailableMinutesPerDay(form);
    const effectiveMaxEmails = getEffectiveMaxEmailsPerDay(form, planMaxDailyLimit);
    const timeGap = form.timeGapMinutes ?? 5;
    const maxEmailsWithGap = effectiveMaxEmails > 0 && timeGap > 0 ? Math.floor(availableMinutes / timeGap) : 0;
    const windowTooShort = effectiveMaxEmails > 0 && timeGap > 0 && maxEmailsWithGap < effectiveMaxEmails;
    const requiredMinutes = effectiveMaxEmails > 0 && timeGap > 0 ? effectiveMaxEmails * timeGap : 0;
    const deliveryWindowError =
      windowTooShort
        ? `Window (${(windowMinutes / 60).toFixed(1)}h) is too short for ${effectiveMaxEmails} emails/day with a ${timeGap}-min gap. Need at least ${formatWindowDuration(requiredMinutes)}.`
        : null;
    const timeGapError =
      windowTooShort
        ? `With a ${timeGap}-min gap and your delivery window you can send at most ${maxEmailsWithGap} emails/day; your daily limit is ${effectiveMaxEmails}. Use a time gap of ${getMaxAllowedTimeGapMinutes(effectiveMaxEmails, availableMinutes)} min or less, or increase the delivery window.`
        : null;

    return {
      esp: espError,
      replyTo: replyToError,
      strategy: strategyError,
      tracking: trackingError,
      schedule: scheduleError,
      deliveryWindow: deliveryWindowError,
      timeGap: timeGapError,
    };
  }, [form, planMaxDailyLimit, customTrackingDomainInput]);

  const canSave = Object.values(inlineErrors).every((e) => !e);

  const handleSave = async () => {
    const total = getChannelMixTotal(form);
    if (total !== 100) {
      toast.error("ESP distribution must total 100%.");
      return;
    }
    const overLimit = CHANNEL_PROVIDER_KEYS.filter((key) => {
      const value = form[key];
      return typeof value === "number" && value > getMaxPercentForProvider(key);
    });
    if (overLimit.length > 0) {
      toast.error(
        "Personal ESPs (Gmail, Outlook, Yahoo, Zoho) are limited to 10% each. Google Workspace and Microsoft 365 are limited to 50% each."
      );
      return;
    }
    if (typeof planMaxDailyLimit === "number" && planMaxDailyLimit > 0) {
      const strategy = form.warmupStrategy ?? "progressive";
      if (strategy === "progressive") {
        const max = form.progressiveMaxLimit ?? 0;
        if (max > planMaxDailyLimit) {
          toast.error(`Progressive max limit cannot exceed plan limit (${planMaxDailyLimit}).`);
          return;
        }
      } else if (strategy === "random") {
        const max = form.randomMaxLimit ?? 0;
        if (max > planMaxDailyLimit) {
          toast.error(`Random max per day cannot exceed plan limit (${planMaxDailyLimit}).`);
          return;
        }
      } else if (strategy === "flat") {
        const c = form.flatConstantEmails ?? 0;
        if (c > planMaxDailyLimit) {
          toast.error(`Flat emails per day cannot exceed plan limit (${planMaxDailyLimit}).`);
          return;
        }
      }
    }
    if (form.enableCustomTrackingDomain) {
      const hasExisting = form.trackingDomainId && form.trackingDomainId !== TRACKING_DOMAIN_PLACEHOLDER_VALUE && form.trackingDomainId !== TRACKING_DOMAIN_ADD_NEW_VALUE;
      const hasCustom = form.trackingDomainId === TRACKING_DOMAIN_ADD_NEW_VALUE && customTrackingDomainInput.trim().length > 0;
      if (!hasExisting && !hasCustom) {
        toast.error("Please select a tracking domain or add a new one.");
        return;
      }
    }
    if (form.replyToEnabled && form.replyToEmails) {
      const emailResult = validEmailFormatSchema.safeParse(form.replyToEmails.trim());
      if (!emailResult.success) {
        toast.error("Please enter a valid reply-to email address.");
        return;
      }
    }
    const isAllDaysSave = form.sendOnDays?.length === 7 && [0, 1, 2, 3, 4, 5, 6].every((d) => form.sendOnDays?.includes(d));
    const isCustomScheduleSave = !form.weekdaysOnly && !isAllDaysSave;
    if (isCustomScheduleSave && (!form.sendOnDays || form.sendOnDays.length === 0)) {
      toast.error("Select at least one day for custom schedule.");
      return;
    }
    const availableMinutes = getAvailableMinutesPerDay(form);
    const effectiveMaxEmails = getEffectiveMaxEmailsPerDay(form, planMaxDailyLimit);
    const timeGap = form.timeGapMinutes ?? 5;
    if (effectiveMaxEmails > 0 && timeGap > 0) {
      const maxEmailsWithGap = Math.floor(availableMinutes / timeGap);
      if (maxEmailsWithGap < effectiveMaxEmails) {
        const maxAllowedGap = getMaxAllowedTimeGapMinutes(effectiveMaxEmails, availableMinutes);
        toast.error(
          `With a ${timeGap}-minute gap and your delivery window you can send at most ${maxEmailsWithGap} emails per day. Choose a time gap of ${maxAllowedGap} minutes or less to fit your daily limit (${effectiveMaxEmails}).`
        );
        return;
      }
    }
    try {
      let formToSave: B2BWarmupSettings =
        form.trackingDomainId === TRACKING_DOMAIN_ADD_NEW_VALUE && customTrackingDomainInput.trim()
          ? { ...form, trackingDomainId: customTrackingDomainInput.trim() }
          : form;
      if (formToSave.trackingDomainId?.trim()) {
        formToSave = {
          ...formToSave,
          trackingDomainOrSubdomain: formToSave.trackingDomainId.trim(),
        };
      }
      const payload = normalizePayloadForSave(formToSave, settingsMode);
      if (onSaveAndEnableWarmup) {
        await onSaveAndEnableWarmup(payload);
        toast.success("Warmup enabled with settings saved");
      } else {
        await saveSettings({ mailboxId, settings: payload });
        toast.success("B2B warmup settings saved successfully");
      }
      if (onMailboxNameChange && (editFirstName !== (mailboxFirstName ?? "") || editLastName !== (mailboxLastName ?? ""))) {
        await onMailboxNameChange(editFirstName.trim(), editLastName.trim());
      }
      onOpenChange(false);
    } catch (error) {
      const msg = extractErrorMessage(error, "Failed to save B2B warmup settings");
      toast.error(msg);
    }
  };

  const actuallyOpen = open && planAssigned;

  return (
    <Dialog open={actuallyOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[60vw] max-h-[90vh] overflow-y-auto"
        showCloseButton={true}
      >
        <DialogHeader className="flex flex-row items-center justify-between pb-2">
          <DialogTitle className="text-lg">
            Warmup B2B Settings
          </DialogTitle>
          
        </DialogHeader>

        {(mailboxEmail || (selectedMailboxCount != null && selectedMailboxCount > 1)) && (
          <div className="bg-muted/50 rounded-md px-3 py-2 text-sm text-muted-foreground">
            {selectedMailboxCount != null && selectedMailboxCount > 1 ? (
              <>Configuring warmup for <span className="font-medium text-foreground">{selectedMailboxCount} mailboxes</span></>
            ) : (
              <>
                Configuring warmup for{" "}
                <span className="font-medium text-foreground">
                  {([editFirstName, editLastName].filter(Boolean).join(" ").trim() || mailboxEmail) && (
                    <>
                      {[editFirstName, editLastName].filter(Boolean).join(" ").trim() || mailboxEmail}
                      {mailboxEmail && ([editFirstName, editLastName].filter(Boolean).join(" ").trim()) && (
                        <span className="text-muted-foreground font-normal"> ({mailboxEmail})</span>
                      )}
                    </>
                  )}
                  {!([editFirstName, editLastName].filter(Boolean).join(" ").trim()) && mailboxEmail}
                </span>
              </>
            )}
          </div>
        )}

        <div className="space-y-4">
        {/* Settings Mode */}
        <div className="rounded-lg border border-border p-4 space-y-2">
          <p className="text-sm font-semibold text-foreground">
            Settings Mode
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={switchToDefault}
              className={cn(
                "flex flex-col items-start rounded-lg border-2 p-3 text-left transition-colors",
                isDefaultMode
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              )}
            >
              <span className="font-medium">Default</span>
              <span className="text-xs text-muted-foreground">
                Per set setting by InboxDoctor
              </span>
            </button>
            <button
              type="button"
              onClick={switchToManual}
              className={cn(
                "flex flex-col items-start rounded-lg border-2 p-3 text-left transition-colors",
                !isDefaultMode
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              )}
            >
              <span className="font-medium">Manual</span>
              <span className="text-xs text-muted-foreground">
                Customize all settings
              </span>
            </button>
          </div>
        </div>

        {/* CHANNEL/PROVIDER MIX */}
        <div
          className={cn(
            "rounded-lg border p-4 space-y-3",
            inlineErrors.esp ? "border-destructive bg-destructive/5" : "border-border"
          )}
        >
          <p className="text-sm font-semibold text-foreground">
            ESP Distribution
          </p>
          <div className="space-y-3">
            {CHANNEL_PROVIDERS.map(({ key, label, iconKey }) => {
              const icon = authProviderIcons[iconKey]?.icon;
              const percentValue = form[key];
              const numValue =
                typeof percentValue === "number" ? percentValue : "";
              const maxAllowed = getMaxAllowedForProvider(form, key);
              return (
                <div
                  key={key}
                  className="flex flex-row items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {icon && (
                      <span className="flex items-center justify-center w-8 h-8 shrink-0 rounded overflow-hidden bg-muted/50">
                        {icon}
                      </span>
                    )}
                    <Label className="text-sm font-medium truncate">
                      {label}
                    </Label>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Input
                      type="number"
                      min={0}
                      max={maxAllowed}
                      value={numValue}
                      onChange={(e) => {
                        const raw = e.target.value === "" ? undefined : Number(e.target.value);
                        if (raw === undefined) {
                          updateField(key, undefined);
                          return;
                        }
                        const providerMax = getMaxPercentForProvider(key);
                        const capped = Math.min(
                          Math.max(0, raw),
                          maxAllowed
                        );
                        if (raw > providerMax) {
                          toast.warning(
                            key === "googleWorkspacePercent" || key === "microsoft365Percent"
                              ? `${CHANNEL_PROVIDERS.find((p) => p.key === key)?.label ?? "This provider"} is limited to ${MAX_PERCENT_WORKSPACE_ESP}%.`
                              : `Personal ESPs are limited to ${MAX_PERCENT_PERSONAL_ESP}% each.`
                          );
                        }
                        updateField(key, capped);
                      }}
                      disabled={isReadOnly}
                      className="h-9 w-24 text-right tabular-nums"
                    />
                    <span className="text-sm text-muted-foreground w-5">%</span>
                  </div>
                </div>
              );
            })}
          </div>
          <p
            className={cn(
              "text-xs",
              getChannelMixTotal(form) > 100
                ? "text-destructive font-medium"
                : getChannelMixTotal(form) === 100
                  ? "text-muted-foreground"
                  : "text-muted-foreground"
            )}
          >
            Total: {getChannelMixTotal(form)}%
            {getChannelMixTotal(form) < 100 && " (must total 100%)"}
          </p>
          {inlineErrors.esp ? (
            <p className="text-xs font-medium text-destructive">{inlineErrors.esp}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Personal ESPs (Gmail, Outlook, Yahoo, Zoho): max {MAX_PERCENT_PERSONAL_ESP}% each. Google Workspace and Microsoft 365: max {MAX_PERCENT_WORKSPACE_ESP}% each.
            </p>
          )}
        </div>

        {/* Reply To */}
        <div
          className={cn(
            "rounded-lg border p-4 space-y-2",
            inlineErrors.replyTo ? "border-destructive bg-destructive/5" : "border-border"
          )}
        >
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Reply To</Label>
              <p className="text-xs text-muted-foreground">
                Enable automatic replies to warmup emails
              </p>
            </div>
            <Switch
              checked={form.replyToEnabled ?? false}
              onCheckedChange={(v) => updateField("replyToEnabled", v)}
              disabled={isReadOnly}
            />
          </div>
          {(form.replyToEnabled ?? false) && (
            <div className="space-y-1 pt-2 border-t">
              <Label className="text-xs">Reply-to email</Label>
              <Input
                type="email"
                placeholder="e.g. reply@yourdomain.com"
                value={form.replyToEmails ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  const single = raw.trim().split(",")[0]?.trim() ?? "";
                  updateField("replyToEmails", single);
                }}
                disabled={isReadOnly}
                className={cn(
                  "h-8",
                  inlineErrors.replyTo && "border-destructive focus-visible:ring-destructive"
                )}
              />
            </div>
          )}
          {inlineErrors.replyTo && (
            <p className="text-xs font-medium text-destructive">{inlineErrors.replyTo}</p>
          )}
        </div>

        {/* WARM-UP STRATEGY */}
        <div
          className={cn(
            "rounded-lg border p-4 space-y-3",
            inlineErrors.strategy ? "border-destructive bg-destructive/5" : "border-border"
          )}
        >
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold text-foreground">
              Warm-Up Strategy
            </p>
            <Info className="h-4 w-4 text-muted-foreground" />
          </div>
          <p className="text-xs text-muted-foreground">
            Choose how your sending volume scales over time.
          </p>

          {(
            [
              {
                id: "progressive" as const,
                title: "Progressive",
                description:
                  "Recommended for new accounts. Gradually increases sending each day until maximum.",
                icon: TrendingUp,
                iconLabel: "Ramping up",
              },
              {
                id: "random" as const,
                title: "Random",
                description:
                  "Human-like activity. Sends a different number of emails each day within a range.",
                icon: Shuffle,
                iconLabel: "Varies daily",
              },
              {
                id: "flat" as const,
                title: "Flat",
                description:
                  "Fixed number of emails every day. Best for accounts with existing reputation.",
                icon: Minus,
                iconLabel: "Constant",
              },
            ] as const
          ).map((strategy) => {
            const selected = (form.warmupStrategy ?? "progressive") === strategy.id;
            return (
              <div
                key={strategy.id}
                className={cn(
                  "rounded-lg border-2 transition-colors",
                  selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                )}
              >
                <button
                  type="button"
                  className="w-full flex items-center gap-3 p-3 text-left"
                  onClick={() => !isReadOnly && updateField("warmupStrategy", strategy.id)}
                  disabled={isReadOnly}
                >
                  <div
                    className={cn(
                      "size-4 rounded-full border-2 shrink-0",
                      selected ? "border-primary bg-primary" : "border-muted-foreground"
                    )}
                  />
                  <strategy.icon
                    className={cn("h-5 w-5 shrink-0", selected ? "text-primary" : "text-muted-foreground")}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{strategy.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {strategy.description}
                    </p>
                  </div>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                      selected && "rotate-180"
                    )}
                  />
                </button>

                {selected && (
                  <div className="border-t border-border px-3 pb-3 pt-2 space-y-3">
                    {strategy.id === "progressive" && (
                      <>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs">Starting volume</Label>
                            <Input
                              type="number"
                              min={0}
                              max={
                                form.progressiveMaxLimit != null
                                  ? planMaxDailyLimit != null
                                    ? Math.min(form.progressiveMaxLimit, planMaxDailyLimit)
                                    : form.progressiveMaxLimit
                                  : planMaxDailyLimit
                              }
                              value={form.progressiveStartingVolume ?? ""}
                              onChange={(e) => {
                                const v = e.target.value === "" ? undefined : Number(e.target.value);
                                const maxCap =
                                  form.progressiveMaxLimit != null
                                    ? planMaxDailyLimit != null
                                      ? Math.min(form.progressiveMaxLimit, planMaxDailyLimit)
                                      : form.progressiveMaxLimit
                                    : planMaxDailyLimit ?? undefined;
                                const capped =
                                  maxCap != null && v != null ? Math.min(v, maxCap) : v;
                                updateField("progressiveStartingVolume", capped);
                              }}
                              disabled={isReadOnly}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Increase by</Label>
                            <Input
                              type="number"
                              min={0}
                              max={
                                form.progressiveMaxLimit != null
                                  ? planMaxDailyLimit != null
                                    ? Math.min(form.progressiveMaxLimit, planMaxDailyLimit)
                                    : form.progressiveMaxLimit
                                  : planMaxDailyLimit
                              }
                              value={form.progressiveIncreaseBy ?? ""}
                              onChange={(e) => {
                                const v = e.target.value === "" ? undefined : Number(e.target.value);
                                const maxCap =
                                  form.progressiveMaxLimit != null
                                    ? planMaxDailyLimit != null
                                      ? Math.min(form.progressiveMaxLimit, planMaxDailyLimit)
                                      : form.progressiveMaxLimit
                                    : planMaxDailyLimit ?? undefined;
                                const capped =
                                  maxCap != null && v != null ? Math.min(v, maxCap) : v;
                                updateField("progressiveIncreaseBy", capped);
                              }}
                              disabled={isReadOnly}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Max limit</Label>
                            <Input
                              type="number"
                              min={0}
                              max={planMaxDailyLimit}
                              value={form.progressiveMaxLimit ?? ""}
                              onChange={(e) => {
                                const v = e.target.value === "" ? undefined : Number(e.target.value);
                                const capped =
                                  planMaxDailyLimit != null && v != null
                                    ? Math.min(v, planMaxDailyLimit)
                                    : v;
                                setForm((prev) => ({
                                  ...prev,
                                  progressiveMaxLimit: capped,
                                  progressiveStartingVolume:
                                    prev.progressiveStartingVolume != null && capped != null && prev.progressiveStartingVolume > capped
                                      ? capped
                                      : prev.progressiveStartingVolume,
                                  progressiveIncreaseBy:
                                    prev.progressiveIncreaseBy != null && capped != null && prev.progressiveIncreaseBy > capped
                                      ? capped
                                      : prev.progressiveIncreaseBy,
                                }));
                              }}
                              disabled={isReadOnly}
                              className="h-8"
                            />
                          </div>
                        </div>
                        <WarmupBarPreview
                          data={getProgressiveBarData(
                            form.progressiveStartingVolume ?? 3,
                            form.progressiveIncreaseBy ?? 3,
                            form.progressiveMaxLimit ?? 25
                          )}
                          maxValue={form.progressiveMaxLimit ?? 25}
                          todayIndex={0}
                          maxReachedIndex={getProgressiveBarData(
                            form.progressiveStartingVolume ?? 3,
                            form.progressiveIncreaseBy ?? 3,
                            form.progressiveMaxLimit ?? 25
                          ).findIndex((v) => v >= (form.progressiveMaxLimit ?? 25))}
                        />
                        <p className="text-xs text-muted-foreground">
                          Sending increases each day until {form.progressiveMaxLimit ?? 25}/day, then stays at max.
                        </p>
                      </>
                    )}

                    {strategy.id === "random" && (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                         
                          <div className="space-y-1">
                            <Label className="text-xs">Max per day</Label>
                            <Input
                              type="number"
                              min={form.randomMinLimit ?? 0}
                              max={planMaxDailyLimit}
                              value={form.randomMaxLimit ?? ""}
                              onChange={(e) => {
                                const v = e.target.value === "" ? undefined : Number(e.target.value);
                                const capped =
                                  planMaxDailyLimit != null && v != null
                                    ? Math.min(v, planMaxDailyLimit)
                                    : v;
                                setForm((prev) => ({
                                  ...prev,
                                  randomMaxLimit: capped,
                                  randomMinLimit:
                                    prev.randomMinLimit != null && capped != null && prev.randomMinLimit > capped
                                      ? capped
                                      : prev.randomMinLimit,
                                }));
                              }}
                              disabled={isReadOnly}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Min per day</Label>
                            <Input
                              type="number"
                              min={0}
                              max={form.randomMaxLimit ?? planMaxDailyLimit}
                              value={form.randomMinLimit ?? ""}
                              onChange={(e) => {
                                const v = e.target.value === "" ? undefined : Number(e.target.value);
                                const maxCap = form.randomMaxLimit ?? planMaxDailyLimit ?? undefined;
                                const capped =
                                  maxCap != null && v != null ? Math.min(v, maxCap) : v;
                                updateField("randomMinLimit", capped);
                              }}
                              disabled={isReadOnly}
                              className="h-8"
                            />
                          </div>
                        </div>
                        <WarmupBarPreview
                          data={getRandomBarData(
                            form.randomMinLimit ?? 5,
                            form.randomMaxLimit ?? 30
                          )}
                          maxValue={form.randomMaxLimit ?? 30}
                          variant="random"
                        />
                        <p className="text-xs text-muted-foreground">
                          Will send between {form.randomMinLimit ?? 5} and {form.randomMaxLimit ?? 30} emails per day.
                        </p>
                      </>
                    )}

                    {strategy.id === "flat" && (
                      <>
                        <div className="space-y-1">
                          <Label className="text-xs">Emails per day</Label>
                          <Input
                            type="number"
                            min={0}
                            max={planMaxDailyLimit}
                            value={form.flatConstantEmails ?? ""}
                            onChange={(e) => {
                              const v = e.target.value === "" ? undefined : Number(e.target.value);
                              const capped =
                                planMaxDailyLimit != null && v != null
                                  ? Math.min(v, planMaxDailyLimit)
                                  : v;
                              updateField("flatConstantEmails", capped);
                            }}
                            disabled={isReadOnly}
                            className="h-8 w-24"
                          />
                        </div>
                        <WarmupBarPreview
                          data={getFlatBarData(form.flatConstantEmails ?? 20)}
                          maxValue={form.flatConstantEmails ?? 20}
                          variant="flat"
                        />
                        <p className="text-xs text-muted-foreground">
                          Will send exactly {form.flatConstantEmails ?? 20} emails every day.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {inlineErrors.strategy && (
            <p className="text-xs font-medium text-destructive">{inlineErrors.strategy}</p>
          )}
        </div>

        {/* Sender name (single mailbox only) */}
        {mailboxEmail && !(selectedMailboxCount != null && selectedMailboxCount > 1) && (
          <div className="rounded-lg border border-border p-4 space-y-3">
            <p className="text-sm font-semibold text-foreground">
              Sender name
            </p>
            <p className="text-xs text-muted-foreground">
              First and last name for this mailbox
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium">First name</Label>
                <Input
                  placeholder="First name"
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                  disabled={isReadOnly}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">Last name</Label>
                <Input
                  placeholder="Last name"
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                  disabled={isReadOnly}
                  className="h-8 text-sm"
                />
              </div>
            </div>
          </div>
        )}

        {/* Warmup Filter Tag */}
        <div className="rounded-lg border border-border p-4 space-y-3">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold text-foreground">
              Warmup Filter Folder
            </p>
            <Info className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Custom Folder</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter custom folder name"
                  value={form.customTag ?? ""}
                  onChange={(e) => updateField("customTag", e.target.value)}
                  disabled={isReadOnly}
                  className="h-9 flex-1 min-w-0"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={handleRefreshDefaultTag}
                  disabled={isReadOnly}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Example: inboxdoctor
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">
                Default tag (auto-generated)
              </Label>
              <div className="flex items-center h-9">
                <span className="flex items-center rounded-md border border-input bg-muted px-3 h-9 text-sm font-mono font-medium min-w-0">
                  {form.defaultTag || "—"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Tracking Domain */}
        <div
          className={cn(
            "rounded-lg border p-4 space-y-3",
            inlineErrors.tracking ? "border-destructive bg-destructive/5" : "border-border"
          )}
        >
          <p className="text-sm font-semibold text-foreground">
            Custom Tracking Domain
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={form.enableCustomTrackingDomain ?? false}
              onCheckedChange={(v) =>
                updateField("enableCustomTrackingDomain", !!v)
              }
              disabled={isReadOnly}
            />
            <span className="text-sm">Enable custom tracking domain</span>
          </label>

          {(form.enableCustomTrackingDomain ?? false) && (
            <>
              <div className="flex items-center justify-between gap-2 pt-1">
                <span className="text-xs text-muted-foreground">
                  Configure DNS and choose a tracking domain below.
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-muted-foreground hover:text-foreground shrink-0"
                  onClick={handleResetCustomTracking}
                  disabled={isReadOnly}
                >
                  <Undo2 className="h-3.5 w-3.5 mr-1" />
                  Reset
                </Button>
              </div>

              {/* Step-by-step DNS instructions */}
              <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
                <p className="text-sm font-medium text-foreground">
                  Step-by-step DNS instructions
                </p>
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <span className="text-xs text-muted-foreground min-w-[5rem]">
                      Record type
                    </span>
                    <span className="font-mono text-sm bg-muted/50 rounded px-2 py-1 border border-border">
                      CNAME
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <span className="text-xs text-muted-foreground min-w-[5rem]">
                      Value
                    </span>
                    <span className="font-mono text-sm bg-muted/50 rounded px-2 py-1 border border-border">
                      trackinboxdoctor.com
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 text-primary shrink-0"
                      onClick={handleCopyDnsValue}
                    >
                      <Copy className="h-3.5 w-3.5 mr-1.5" />
                      Copy
                    </Button>
                  </div>
                </div>
              </div>

              {/* Select or add tracking domain */}
              <div className="space-y-2">
                <Label className="text-sm">Select or add tracking domain</Label>
                <Select
                  value={
                    form.trackingDomainId && form.trackingDomainId !== ""
                      ? form.trackingDomainId
                      : TRACKING_DOMAIN_PLACEHOLDER_VALUE
                  }
                  onValueChange={(v) => {
                    updateField(
                      "trackingDomainId",
                      v === TRACKING_DOMAIN_PLACEHOLDER_VALUE ? undefined : v
                    );
                    if (v !== TRACKING_DOMAIN_ADD_NEW_VALUE) {
                      setCustomTrackingDomainInput("");
                    }
                  }}
                  disabled={isReadOnly}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select a tracking domain" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRACKING_DOMAIN_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.trackingDomainId === TRACKING_DOMAIN_ADD_NEW_VALUE && (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">
                      New domain (e.g. track.yourdomain.com)
                    </Label>
                    <Input
                      placeholder="Enter tracking domain"
                      value={customTrackingDomainInput}
                      onChange={(e) =>
                        setCustomTrackingDomainInput(e.target.value)
                      }
                      disabled={isReadOnly}
                      className="h-9 font-mono text-sm"
                    />
                  </div>
                )}
                {form.enableCustomTrackingDomain &&
                  !form.trackingDomainId && (
                    <p className="text-xs text-destructive">
                      Please select a tracking domain or add a new one.
                    </p>
                  )}
                {form.enableCustomTrackingDomain &&
                  form.trackingDomainId === TRACKING_DOMAIN_ADD_NEW_VALUE &&
                  !customTrackingDomainInput.trim() && (
                    <p className="text-xs text-destructive">
                      Please enter your tracking domain.
                    </p>
                  )}
              </div>
            </>
          )}
          {inlineErrors.tracking && (
            <p className="text-xs font-medium text-destructive">{inlineErrors.tracking}</p>
          )}
        </div>

        {/* Timezone, Schedule & Delivery Window */}
        <div className="rounded-lg border border-border p-4 space-y-4">
        {/* Timezone */}
        <div className="space-y-1">
          <Label className="text-sm font-semibold text-foreground">Timezone</Label>
          <Select
            value={form.timezone ?? ""}
            onValueChange={(v) => updateField("timezone", v)}
            disabled={isReadOnly}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Scheduled Warmup: Weekdays / All days / Custom */}
        <div
          className={cn(
            "space-y-3",
            inlineErrors.schedule && "rounded-lg border-2 border-destructive bg-destructive/5 p-4"
          )}
        >
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold text-foreground">
              Scheduled Warmup
            </p>
            <Info className="h-4 w-4 text-muted-foreground" />
          </div>
          <p className="text-xs text-muted-foreground">
            Configure days when you want to send warmup emails.
          </p>

          {(
            [
              {
                id: "alldays" as const,
                title: "All days",
                description: "Send warmup every day of the week.",
                icon: CalendarRange,
                iconLabel: "Every day",
              },
              {
                id: "weekdays" as const,
                title: "Weekdays",
                description: "Send warmup Monday–Friday only.",
                icon: CalendarDays,
                iconLabel: "Mon–Fri",
              },
              {
                id: "custom" as const,
                title: "Custom",
                description: "Choose specific days to send warmup.",
                icon: Calendar,
                iconLabel: "Pick days",
              },
            ] as const
          ).map((schedule) => {
            const isAllDaysSelection = form.sendOnDays?.length === 7 && [0, 1, 2, 3, 4, 5, 6].every((d) => form.sendOnDays?.includes(d));
            const scheduleType =
              form.weekdaysOnly
                ? "weekdays"
                : isAllDaysSelection
                  ? "alldays"
                  : "custom";
            const selected = scheduleType === schedule.id;
            return (
              <div
                key={schedule.id}
                className={cn(
                  "rounded-lg border-2 transition-colors",
                  selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                )}
              >
                <button
                  type="button"
                  className="w-full flex items-center gap-3 p-3 text-left"
                  onClick={() => {
                    if (isReadOnly) return;
                    if (schedule.id === "weekdays") {
                      updateField("weekdaysOnly", true);
                      updateField("sendOnDays", WEEKDAY_DAYS);
                    } else if (schedule.id === "alldays") {
                      updateField("weekdaysOnly", false);
                      updateField("sendOnDays", ALL_DAYS);
                    } else {
                      updateField("weekdaysOnly", false);
                      const current = form.sendOnDays;
                      const next = current?.length ? [...current] : WEEKDAY_DAYS;
                      updateField("sendOnDays", next);
                    }
                  }}
                  disabled={isReadOnly}
                >
                  <div
                    className={cn(
                      "size-4 rounded-full border-2 shrink-0",
                      selected ? "border-primary bg-primary" : "border-muted-foreground"
                    )}
                  />
                  <schedule.icon
                    className={cn("h-5 w-5 shrink-0", selected ? "text-primary" : "text-muted-foreground")}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{schedule.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {schedule.description}
                    </p>
                  </div>
                  {schedule.id === "custom" && (
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                        selected && "rotate-180"
                      )}
                    />
                  )}
                </button>

                {schedule.id === "custom" && selected && (
                  <div className="border-t border-border px-3 pb-3 pt-2 space-y-2">
                    <p className="text-xs text-muted-foreground">Send on</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      {[0, 1, 2, 3, 4, 5, 6].map((day) => {
                        const days = form.sendOnDays ?? WEEKDAY_DAYS;
                        const checked = days.includes(day);
                        return (
                          <label
                            key={day}
                            className="flex items-center gap-2 cursor-pointer text-sm"
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => {
                                if (isReadOnly) return;
                                const next = checked
                                  ? days.filter((d) => d !== day)
                                  : [...days, day].sort((a, b) => a - b);
                                updateField("sendOnDays", next.length > 0 ? next : [day]);
                              }}
                              disabled={isReadOnly}
                            />
                            <span>{DAY_LABELS[day]}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {inlineErrors.schedule && (
            <p className="text-xs font-medium text-destructive">{inlineErrors.schedule}</p>
          )}
        </div>

        {/* Daily email delivery period: From / To; window must fit sending volume and time gap */}
        <div
          className={cn(
            "rounded-lg border bg-muted/20 p-4 space-y-4",
            inlineErrors.deliveryWindow ? "border-destructive bg-destructive/5" : "border-border"
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-semibold text-foreground">
                  Daily Email Delivery Period
                </p>
                <Info className="h-4 w-4 text-muted-foreground shrink-0" />
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Time window each day when warmup emails can be sent. It must be long enough to fit your daily sending volume and time gap (see Engagement rates).
              </p>
            </div>
            {!isReadOnly && (
              <div className="flex gap-1 shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    updateField("deliveryWindowStartMinutes", DEFAULT_DELIVERY_WINDOW_START);
                    updateField("deliveryWindowEndMinutes", 14 * 60);
                  }}
                >
                  Use 14 hours
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    updateField("deliveryWindowStartMinutes", DEFAULT_DELIVERY_WINDOW_START);
                    updateField("deliveryWindowEndMinutes", MINUTES_PER_DAY);
                  }}
                >
                  Use 24 hours
                </Button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1.5 min-w-0">
              <Label className="text-xs font-medium text-muted-foreground">From</Label>
              <Select
                value={String(form.deliveryWindowStartMinutes ?? DEFAULT_DELIVERY_WINDOW_START)}
                onValueChange={(v) => {
                  updateField("deliveryWindowStartMinutes", Number(v));
                }}
                disabled={isReadOnly}
              >
                <SelectTrigger className="h-9 w-full">
                  <SelectValue>
                    {DELIVERY_WINDOW_TIME_OPTIONS.find(
                      (o) => o.value === (form.deliveryWindowStartMinutes ?? DEFAULT_DELIVERY_WINDOW_START)
                    )?.label ?? "Start time"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {DELIVERY_WINDOW_TIME_OPTIONS.filter((o) => o.value < MINUTES_PER_DAY).map((opt) => (
                    <SelectItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 min-w-0">
              <Label className="text-xs font-medium text-muted-foreground">To</Label>
              <Select
                value={String(form.deliveryWindowEndMinutes ?? DEFAULT_DELIVERY_WINDOW_END)}
                onValueChange={(v) => {
                  updateField("deliveryWindowEndMinutes", Number(v));
                }}
                disabled={isReadOnly}
              >
                <SelectTrigger className="h-9 w-full">
                  <SelectValue>
                    {DELIVERY_WINDOW_TIME_OPTIONS.find(
                      (o) => o.value === (form.deliveryWindowEndMinutes ?? DEFAULT_DELIVERY_WINDOW_END)
                    )?.label ?? "End time"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {DELIVERY_WINDOW_TIME_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {(() => {
            const start = form.deliveryWindowStartMinutes ?? DEFAULT_DELIVERY_WINDOW_START;
            const end = form.deliveryWindowEndMinutes ?? DEFAULT_DELIVERY_WINDOW_END;
            const span = end > start ? end - start : end < start ? MINUTES_PER_DAY - start + end : MINUTES_PER_DAY;
            const startLabel = DELIVERY_WINDOW_TIME_OPTIONS.find((o) => o.value === start)?.label ?? "12:00 AM";
            const endLabel = DELIVERY_WINDOW_TIME_OPTIONS.find((o) => o.value === end)?.label ?? "12:00 AM";
            const durationText = formatWindowDuration(span);
            const effectiveMaxEmails = getEffectiveMaxEmailsPerDay(form, planMaxDailyLimit);
            const timeGap = form.timeGapMinutes ?? 5;
            const fitsVolumeAndGap = effectiveMaxEmails <= 0 || timeGap <= 0 || Math.floor(span / timeGap) >= effectiveMaxEmails;
            return (
              <div className={cn("rounded-md border px-3 py-2", fitsVolumeAndGap ? "border-border bg-background/50" : "border-destructive/50 bg-destructive/5")}>
                <p className="text-sm text-foreground">
                  Send between <span className="font-semibold text-primary">{startLabel}</span> and <span className="font-semibold text-primary">{endLabel}</span>
                  <span className="text-muted-foreground"> ({durationText})</span>
                </p>
                <p className={cn("text-xs mt-0.5", fitsVolumeAndGap ? "text-muted-foreground" : "text-destructive")}>
                  {fitsVolumeAndGap
                    ? "Time gap below is calculated for this window."
                    : "Window is too short for your daily sending limit and time gap. Widen the window or reduce limit/gap."}
                </p>
              </div>
            );
          })()}
          {inlineErrors.deliveryWindow && (
            <p className="text-xs font-medium text-destructive">{inlineErrors.deliveryWindow}</p>
          )}
        </div>
        </div>

        {/* Engagement Rates - separate block */}
        <div className="rounded-lg border border-border p-4 space-y-4">
          <p className="text-sm font-semibold text-foreground">
            Engagement Rates
          </p>
          <div className="grid grid-cols-2 gap-3">
            {/* <div className="space-y-1">
              <Label className="text-xs">Daily warmup limit</Label>
              <Input
                type="number"
                min={0}
                max={planMaxDailyLimit}
                value={form.dailyWarmupLimit ?? ""}
                onChange={(e) => {
                  const v = e.target.value === "" ? undefined : Number(e.target.value);
                  const capped =
                    planMaxDailyLimit != null && v != null
                      ? Math.min(v, planMaxDailyLimit)
                      : v;
                  updateField("dailyWarmupLimit", capped);
                }}
                disabled={isReadOnly}
                className="h-8"
              />
            </div> */}
            <div className="space-y-1">
              <Label className="text-xs">Reply Rate %</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={form.replyRatePercent ?? ""}
                onChange={(e) =>
                  updateField(
                    "replyRatePercent",
                    e.target.value === "" ? undefined : Number(e.target.value)
                  )
                }
                disabled={isReadOnly}
                className="h-8"
              />
              <p className="text-xs text-muted-foreground">Suggested: 30</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Open Rates (%)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={form.openRatesPercent ?? ""}
                onChange={(e) =>
                  updateField(
                    "openRatesPercent",
                    e.target.value === "" ? undefined : Number(e.target.value)
                  )
                }
                disabled={isReadOnly}
                className="h-8"
              />
              <p className="text-xs text-muted-foreground">
                How many of your warmup emails to open
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Spam Protection (%)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={form.spamProtectionPercent ?? ""}
                onChange={(e) =>
                  updateField(
                    "spamProtectionPercent",
                    e.target.value === "" ? undefined : Number(e.target.value)
                  )
                }
                disabled={isReadOnly}
                className="h-8"
              />
              <p className="text-xs text-muted-foreground">
                How many of your warmup emails to save from spam folder
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Mark Important (%)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={form.markImportantPercent ?? ""}
                onChange={(e) =>
                  updateField(
                    "markImportantPercent",
                    e.target.value === "" ? undefined : Number(e.target.value)
                  )
                }
                disabled={isReadOnly}
                className="h-8"
              />
              <p className="text-xs text-muted-foreground">
                How many of your warmup emails are marked as Important
              </p>
            </div>
            <div
              className={cn(
                "space-y-1 rounded-md p-2 -m-2",
                inlineErrors.timeGap && "border border-destructive bg-destructive/5"
              )}
            >
              <Label className="text-xs">Time Gap</Label>
              {(() => {
                const availableMinutes = getAvailableMinutesPerDay(form);
                const effectiveMaxEmails = getEffectiveMaxEmailsPerDay(form, planMaxDailyLimit);
                const maxAllowedGap =
                  effectiveMaxEmails > 0 ? getMaxAllowedTimeGapMinutes(effectiveMaxEmails, availableMinutes) : 60;
                const allowedTimeGapOptions = TIME_GAP_OPTIONS.filter(
                  (opt) => Number(opt.value) <= maxAllowedGap
                );
                const options = allowedTimeGapOptions.length > 0 ? allowedTimeGapOptions : TIME_GAP_OPTIONS;
                const currentGap = form.timeGapMinutes ?? 5;
                const validValue =
                  options.some((o) => Number(o.value) === currentGap)
                    ? currentGap
                    : Number(options[options.length - 1]?.value ?? 5);
                return (
                  <>
                    <Select
                      value={String(validValue)}
                      onValueChange={(v) =>
                        updateField("timeGapMinutes", v === "" ? undefined : Number(v))
                      }
                      disabled={isReadOnly}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {options.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      How much delay between each email. Linked to your delivery window above.
                      {effectiveMaxEmails > 0 ? (
                        <> With your daily limit (max {effectiveMaxEmails}/day) and window, only gaps ≤ {maxAllowedGap} min fit.</>
                      ) : (
                        <> Set your strategy limits above first.</>
                      )}
                    </p>
                    {inlineErrors.timeGap && (
                      <p className="text-xs font-medium text-destructive">{inlineErrors.timeGap}</p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!canSave || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
